// src/components/UserProfile.js
import React, { useState } from 'react';
import { api } from '../services/api';

const UserProfile = ({ user }) => {
  const userData = user?.data || user || {};

  const [formData, setFormData] = useState({
    firstName: userData.firstName || '',
    lastName: userData.lastName || '',
    email: userData.email || '',
    phone: userData.phone || '',
    hospitalName: userData.hospitalName || '',
    department: userData.department || '',
    serviceNumber: userData.serviceNumber || '',
  });

  const [passwordData, setPasswordData] = useState({
    old: '',
    new: '',
    confirm: '',
  });
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setMessage('');
    setIsError(false);

    try {
      const updatePayload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        hospitalName: formData.hospitalName,
        department: formData.department,
        serviceNumber: formData.serviceNumber,
      };

      const userId = userData.id || userData.uid || userData._id;
      await api.updateUserProfile(userId, updatePayload);

      // Refresh stored user so top-bar updates immediately
      const updated = { ...userData, ...updatePayload };
      localStorage.setItem('user', JSON.stringify(updated));

      setMessage('Profile updated successfully!');
    } catch (err) {
      setIsError(true);
      setMessage(`Error updating profile: ${err.message}`);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage('');
    setIsError(false);

    if (passwordData.new !== passwordData.confirm) {
      setIsError(true);
      setMessage('New passwords do not match');
      return;
    }
    if (!passwordData.new || passwordData.new.length < 8) {
      setIsError(true);
      setMessage('Password must be at least 8 characters');
      return;
    }

    try {
      const userId = userData.id || userData.uid || userData._id;
      await api.changePassword(userId, passwordData.old, passwordData.new);
      setMessage('Password changed successfully!');
      setPasswordData({ old: '', new: '', confirm: '' });
    } catch (err) {
      setIsError(true);
      const msg = err.message || '';
      if (msg.toLowerCase().includes('not registered') || msg.includes('404')) {
        setMessage(
          'Password change is not yet available on the backend. Please contact your administrator.'
        );
      } else {
        setMessage(`Error changing password: ${msg}`);
      }
    }
  };

  return (
    <div
      className="view-container fade-in"
      style={{
        maxWidth: '850px',
        margin: '0 auto',
        fontFamily: "'Inter', sans-serif",
        padding: '20px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '25px',
        }}
      >
        <div>
          <h2 style={{ margin: '0 0 5px 0', fontSize: '1.8rem', color: '#0f172a' }}>
            My Profile
          </h2>
          <p style={{ margin: 0, color: '#64748b', fontSize: '0.95rem' }}>
            Manage your personal details and security preferences.
          </p>
        </div>

        <div
          style={{
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            maxWidth: '300px',
          }}
        >
          <span
            style={{
              ...badgeStyle,
              background: '#eff6ff',
              color: '#1d4ed8',
              border: '1px solid #bfdbfe',
            }}
          >
            {userData.role?.replace('_', ' ') || 'USER'}
          </span>
          <span
            style={{
              ...badgeStyle,
              background: '#f8fafc',
              color: '#475569',
              border: '1px solid #e2e8f0',
            }}
          >
            {userData.designation?.replace('_', ' ') || 'CLINICIAN'}
          </span>
          <span
            style={{
              ...badgeStyle,
              background: userData.emailVerified ? '#f0fdf4' : '#fff7ed',
              color: userData.emailVerified ? '#16a34a' : '#c2410c',
              border: `1px solid ${
                userData.emailVerified ? '#bbf7d0' : '#fed7aa'
              }`,
            }}
          >
            {userData.emailVerified ? '✓ Email Verified' : '⚠ Email Unverified'}
          </span>
        </div>
      </div>

      {message && (
        <p
          style={{
            color: isError ? '#ef4444' : '#16a34a',
            background: isError ? '#fef2f2' : '#f0fdf4',
            padding: '12px 16px',
            borderRadius: '6px',
            border: `1px solid ${isError ? '#fecaca' : '#bbf7d0'}`,
            fontWeight: '500',
          }}
        >
          {message}
        </p>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.2fr 1fr',
          gap: '30px',
          marginTop: '20px',
        }}
      >
        <div
          style={{
            background: 'white',
            padding: '30px',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 6px rgba(0,0,0,0.02)',
          }}
        >
          <h3
            style={{
              margin: '0 0 20px 0',
              fontSize: '1.2rem',
              color: '#1e293b',
            }}
          >
            Personal Details
          </h3>
          <form
            onSubmit={handleUpdateProfile}
            style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}
          >
            <div style={{ display: 'flex', gap: '15px' }}>
              <label style={labelStyle}>
                First Name
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) =>
                    setFormData({ ...formData, firstName: e.target.value })
                  }
                  style={inputStyle}
                />
              </label>
              <label style={labelStyle}>
                Last Name
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) =>
                    setFormData({ ...formData, lastName: e.target.value })
                  }
                  style={inputStyle}
                />
              </label>
            </div>
            <label style={labelStyle}>
              Email Address (Read Only)
              <input
                type="email"
                value={formData.email}
                disabled
                style={{
                  ...inputStyle,
                  background: '#f8fafc',
                  color: '#94a3b8',
                  cursor: 'not-allowed',
                }}
              />
            </label>
            <label style={labelStyle}>
              Phone Number
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              Hospital / Clinic Name
              <input
                type="text"
                value={formData.hospitalName}
                onChange={(e) =>
                  setFormData({ ...formData, hospitalName: e.target.value })
                }
                style={inputStyle}
              />
            </label>
            <div style={{ display: 'flex', gap: '15px' }}>
              <label style={labelStyle}>
                Department
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) =>
                    setFormData({ ...formData, department: e.target.value })
                  }
                  style={inputStyle}
                />
              </label>
              <label style={labelStyle}>
                Service / ID Number
                <input
                  type="text"
                  value={formData.serviceNumber}
                  onChange={(e) =>
                    setFormData({ ...formData, serviceNumber: e.target.value })
                  }
                  style={inputStyle}
                />
              </label>
            </div>
            <button
              type="submit"
              className="btn-primary"
              style={{ padding: '12px', marginTop: '10px', fontSize: '0.95rem' }}
            >
              Save Changes
            </button>
          </form>
        </div>

        <div
          style={{
            background: 'white',
            padding: '30px',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 6px rgba(0,0,0,0.02)',
            alignSelf: 'start',
          }}
        >
          <h3
            style={{
              margin: '0 0 20px 0',
              fontSize: '1.2rem',
              color: '#1e293b',
            }}
          >
            Security &amp; Authentication
          </h3>
          <form
            onSubmit={handleChangePassword}
            style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}
          >
            <label style={labelStyle}>
              Current Password
              <input
                type="password"
                value={passwordData.old}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, old: e.target.value })
                }
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              New Password
              <input
                type="password"
                value={passwordData.new}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, new: e.target.value })
                }
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              Confirm New Password
              <input
                type="password"
                value={passwordData.confirm}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, confirm: e.target.value })
                }
                style={inputStyle}
              />
            </label>
            <button
              type="submit"
              className="btn-secondary"
              style={{
                padding: '12px',
                marginTop: '10px',
                fontSize: '0.95rem',
                border: '1px solid #cbd5e1',
                background: '#f8fafc',
                color: '#0f172a',
              }}
            >
              Update Password
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  fontSize: '0.85rem',
  fontWeight: '600',
  color: '#475569',
  flex: 1,
};
const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: '6px',
  border: '1px solid #cbd5e1',
  marginTop: '6px',
  fontSize: '0.95rem',
  outlineColor: '#3b82f6',
  boxSizing: 'border-box',
};
const badgeStyle = {
  padding: '4px 10px',
  borderRadius: '20px',
  fontSize: '0.7rem',
  fontWeight: '700',
  textTransform: 'uppercase',
};

export default UserProfile;